import type { Aktuell, Job, Page, Product, Project } from '@/payload-types'

type MaybeDoc = Partial<Page | Product | Project | Job | Aktuell> | null | undefined

/**
 * Resolves the canonical URL path for a Payload document.
 *
 * For Pages, the authoritative source is `breadcrumbs[last].url` — populated by
 * the nested-docs plugin via `generateURL` in src/plugins/index.ts. Every other
 * consumer (canonical, sitemap, SEO plugin meta.url) must read from the same
 * source to stay consistent.
 *
 * For collection docs that live under a fixed URL prefix (Products, Projects,
 * Jobs), the prefix is derived from the collection.
 */
export function getDocUrlPath(doc: MaybeDoc): string {
  if (!doc) return '/'

  // Pages: breadcrumbs are authoritative (covers top-level and nested)
  const breadcrumbs = (doc as Page).breadcrumbs
  if (Array.isArray(breadcrumbs) && breadcrumbs.length > 0) {
    const last = breadcrumbs[breadcrumbs.length - 1]?.url
    if (last) return last
  }

  // Home variants (only reachable when breadcrumbs are missing)
  if (doc.slug === 'home' || doc.slug === 'startseite' || !doc.slug) return '/'

  const slug = Array.isArray(doc.slug) ? doc.slug.join('/') : String(doc.slug)

  // Collection-prefixed URLs (duck-typed on collection-specific markers)
  if ('projectDate' in doc) return `/projekte/${slug}`
  if ('type' in doc && (doc as Product).type === 'internal') return `/produkte/${slug}`
  if ('department' in doc || 'workload' in doc) return `/karriere/jobs/${slug}`
  if ('category' in doc && 'featuredImage' in doc && 'shortDescription' in doc)
    return `/aktuell/${slug}`

  return `/${slug}`
}
